/**
 * M11: SVG rendering of the shared ship designs (graphics/ship-art.ts): fleet cards, system panel, previews.
 * Decorative only — every number shown next to a ship comes from server data.
 */
import { memo, useId } from 'react';
import { ART_BOX, SHIP_ROLE, designOf, shipPalette, toneColor, visibleDamageSpots, type Layer, type ShipClass, type Tone } from './ship-art.ts';
import { cssOf, mixHex, type Role } from '../design/tokens.ts';

const CRUISER_FACTOR = 1.45;

export interface ShipArtProps {
  cls: ShipClass | string;
  /** Colour code; defaults to the class's own role (scout = survey, construction = build, warships = military) */
  role?: Role;
  /** Box size in CSS px */
  size?: number;
  /** true: classes keep their relative sizes (a cruiser fills the box, a scout is smaller); false: always fill the box */
  relative?: boolean;
  /** engine flame (animated unless reduced motion) */
  thrust?: boolean;
  /** 0..1, derived from real hull values by the caller */
  damage?: number;
  /** degrees, 0 = nose up */
  rotate?: number;
  className?: string;
  title?: string;
}

function LayerSvg({ layer, pal, grads, i }: { layer: Layer; pal: ReturnType<typeof shipPalette>; grads: Record<string, string>; i: number }) {
  const fillOf = (t: Tone) => grads[t] ?? cssOf(toneColor(pal, t));
  switch (layer.kind) {
    case 'poly':
      return <polygon key={i} points={layer.pts.join(' ')} fill={fillOf(layer.fill)} fillOpacity={layer.alpha ?? 1}
        stroke={layer.stroke ? cssOf(toneColor(pal, layer.stroke)) : 'none'} strokeWidth={0.02} strokeLinejoin="round" />;
    case 'circle':
      return <circle key={i} cx={layer.cx} cy={layer.cy} r={layer.r} fill={fillOf(layer.fill)} stroke={layer.stroke ? cssOf(toneColor(pal, layer.stroke)) : 'none'} strokeWidth={0.02} />;
    case 'ellipse':
      return <ellipse key={i} cx={layer.cx} cy={layer.cy} rx={layer.rx} ry={layer.ry} fill={fillOf(layer.fill)} />;
    case 'line':
      return <polyline key={i} points={layer.pts.join(' ')} fill="none" stroke={cssOf(toneColor(pal, layer.stroke))} strokeWidth={layer.width} strokeLinecap="round" />;
  }
}

export const ShipArt = memo(function ShipArt({ cls, role, size = 48, relative = true, thrust = false, damage = 0, rotate = 0, className, title }: ShipArtProps) {
  const uid = useId().replace(/:/g, '');
  const design = designOf(cls);
  const pal = shipPalette(role ?? SHIP_ROLE[design.cls]);
  const k = relative ? design.sizeFactor / CRUISER_FACTOR : 1;
  const grads: Record<string, string> = {};
  const gradDefs = (['hull', 'hullLight', 'hullDark'] as const).map(t => {
    const base = pal[t];
    grads[t] = `url(#${uid}-${t})`;
    return (
      <linearGradient key={t} id={`${uid}-${t}`} x1="0" y1="0" x2="1" y2="1">
        <stop offset="0" stopColor={cssOf(mixHex(base, 0xffffff, 0.18))} />
        <stop offset="1" stopColor={cssOf(mixHex(base, 0x000000, 0.28))} />
      </linearGradient>
    );
  });
  const label = title ?? design.label;
  const spots = visibleDamageSpots(damage, design.damageSpots.length);
  const flameColor = cssOf(design.flame.color);
  return (
    <svg width={size} height={size} viewBox={`${ART_BOX.x} ${ART_BOX.y} ${ART_BOX.w} ${ART_BOX.h}`} role="img" aria-label={label}
      className={className} data-ship-art={design.cls} style={{ overflow: 'visible', flexShrink: 0 }}>
      <title>{label}</title>
      <defs>
        {gradDefs}
        <radialGradient id={`${uid}-flame`}>
          <stop offset="0" stopColor={cssOf(design.flame.core)} stopOpacity="0.95" />
          <stop offset="0.5" stopColor={flameColor} stopOpacity="0.7" />
          <stop offset="1" stopColor={flameColor} stopOpacity="0" />
        </radialGradient>
      </defs>
      <g transform={`rotate(${rotate}) scale(${k})`}>
        {thrust && design.nozzles.map((n, i) => (
          <ellipse key={`f${i}`} className="engine-glow" cx={n.x} cy={n.y + design.flame.length * 0.5} rx={n.w * 1.1} ry={design.flame.length * 0.62}
            fill={`url(#${uid}-flame)`} />
        ))}
        {!thrust && design.nozzles.map((n, i) => (
          <ellipse key={`g${i}`} cx={n.x} cy={n.y + 0.05} rx={n.w * 0.9} ry={0.08} fill={`url(#${uid}-flame)`} opacity={0.55} />
        ))}
        {design.layers.map((l, i) => <LayerSvg key={i} layer={l} pal={pal} grads={grads} i={i} />)}
        {design.damageSpots.slice(0, spots).map((s, i) => (
          <g key={`d${i}`}>
            <circle cx={s.x} cy={s.y} r={s.r} fill={cssOf(pal.burn)} opacity={0.62} />
            {damage > 0.5 && <circle cx={s.x} cy={s.y} r={s.r * 0.4} fill={cssOf(pal.ember)} opacity={0.85} />}
            {damage > 0.35 && <polyline points={`${s.x - s.r} ${s.y - s.r * 0.4} ${s.x} ${s.y} ${s.x + s.r * 0.9} ${s.y + s.r}`} fill="none" stroke={cssOf(pal.burn)} strokeWidth={0.025} />}
          </g>
        ))}
      </g>
    </svg>
  );
});

/**
 * A fleet's composition as a small formation: the biggest ship in front, the other classes behind it,
 * with the real ship count of each class. `shipCounts` is server data; the arrangement is decorative.
 */
export const FleetFormation = memo(function FleetFormation({ shipCounts, role, size = 56, damage = 0, thrust = false }: {
  shipCounts: Record<string, number>; role?: Role; size?: number; damage?: number; thrust?: boolean;
}) {
  const classes = (['cruiser', 'frigate', 'corvette', 'construction', 'scout'] as const).filter(c => (shipCounts[c] ?? 0) > 0);
  if (classes.length === 0) return <ShipArt cls="corvette" role="neutral" size={size} title="Üres flotta" damage={0} />;
  const lead = classes[0]!;
  const rest = classes.slice(1, 4);
  const sub = Math.round(size * 0.52);
  return (
    <span className="relative inline-block flex-shrink-0" style={{ width: size, height: size }} data-formation={classes.join(',')}>
      {rest.map((c, i) => (
        <span key={c} className="absolute" style={{ left: i % 2 === 0 ? -sub * 0.15 : size - sub * 0.85, top: size * 0.32 + i * size * 0.1, opacity: 0.9 }}>
          <ShipArt cls={c} role={role ?? SHIP_ROLE[c]} size={sub} damage={damage} />
        </span>
      ))}
      <span className="absolute inset-0"><ShipArt cls={lead} role={role ?? SHIP_ROLE[lead]} size={size} thrust={thrust} damage={damage} /></span>
    </span>
  );
});
